'use client'
import type { ProjectSettingsContextProps } from '@/providers/project-settings/project-settings.types'

import React, { createContext, useContext } from 'react'

const ProjectSettingsContext = createContext<ProjectSettingsContextProps>({})

/**
 * Use this provider to add more project related settings to the context.
 * This is useful for settings that are not directly related to the current page or component,
 * but are needed across the application. For example, header settings, footer settings, or other global settings.
 *
 * Add types to the `ProjectSettingsContextProps` interface in `project-settings.types.ts` file.
 * Provide value to the context in the `ProjectSettingsProvider` component in Root Layout.
 */
export const ProjectSettingsProvider = ({
  children,
  projectSettings,
}: {
  children: React.ReactNode
  projectSettings: ProjectSettingsContextProps
}) => {
  return (
    <ProjectSettingsContext.Provider value={projectSettings}>
      {children}
    </ProjectSettingsContext.Provider>
  )
}

export const useProjectSettingsContext = () => {
  const context = useContext(ProjectSettingsContext)
  if (!context) {
    throw new Error(
      'useProjectSettingsContext must be used within a ProjectSettingsProvider'
    )
  }
  return context
}
